import { faCamera, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "context/AuthContext";
import { useLanguage } from "context/LanguageContext";
import { useEffect, useRef, useState } from "react";
import { fetchEmployeeData, fetchStudentData } from "services/data";
import { uploadUserImage } from "services/admins";
import { fetchWisdomUserImage } from "services/wisdom/wisdomData";
import toast from "react-hot-toast";

const API_BASE = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:4000";

const UserProfileData = ({ userId, role }) => {
    const { userInfo } = useAuth();
    const { language } = useLanguage();
    const [userData, setUserData] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const loadUserData = async () => {
            try {
                let response
                if (role === "Student") {
                    response = await fetchStudentData(userInfo, userId);
                } else {
                    response = await fetchEmployeeData(userInfo, userId);
                }
                setUserData(response || {});
            } catch (error) {
                console.error("Failed to load employee data:", error);
            }
        };

        if (userInfo?.token) {
            loadUserData();
        }
    }, [userInfo]);

    const loadProfileImage = async () => {
        if (!userId) return;
        try {
            const userImage = await fetchWisdomUserImage(userId);
            if (userImage?.file_path) {
                const safePath = userImage.file_path.replace(/\\/g, "/");
                const fileName = safePath.split("/").pop();
                setProfileImage(`${API_BASE}/api/v1/files/open/${encodeURIComponent(fileName)}?t=${Date.now()}`);
            } else {
                setProfileImage(null);
            }
        } catch (error) {
            setProfileImage(null);
        }
    };

    useEffect(() => {
        loadProfileImage();
    }, [userId]);

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("user_id", Number(userId));
        formData.append("file", file);

        setUploadingImage(true);
        try {
            await uploadUserImage(formData);
            toast.success(language ? "Image uploaded" : "تم رفع الصورة");
            await loadProfileImage();
        } catch (error) {
            toast.error(language ? "Failed to upload image" : "فشل رفع الصورة");
        } finally {
            setUploadingImage(false);
            e.target.value = "";
        }
    };

    const valueOrDots = (value) => value || "........................";
    return (
        <div className="grid md:grid-cols-2 grid-cols-1 gap-4" dir={language ? "ltr" : "rtl"}>
            {/* Profile image and name and title */}
            <div className="bg-white dark:bg-darkCard rounded-2xl shadow-xl p-6">
                <div className="text-center">
                    <div className="relative inline-block mb-4">
                        {profileImage ? (
                            <img
                                src={profileImage}
                                alt="profile"
                                className="w-32 h-32 rounded-full object-cover"
                                onError={() => setProfileImage(null)}
                            />
                        ) : (
                            <div className="w-32 h-32 bg-gradient-to-br from-watomsBlue to-wisdomOrange rounded-full flex items-center justify-center text-white text-4xl font-bold">
                                {userData?.first_name?.[0]}{userData?.last_name?.[0]}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="absolute bottom-0 right-0 bg-white dark:bg-darkCard rounded-full p-2 shadow-lg disabled:opacity-60"
                            title={language ? "Change photo" : "تغيير الصورة"}
                        >
                            <FontAwesomeIcon icon={faCamera} className="text-watomsBlue" />
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleImageChange}
                        />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-darkText mb-2">
                        {userData?.first_name} {userData?.middle_name} {userData?.last_name}
                    </h2>
                    {role === "Student" ?
                        <p className="text-gray-600 dark:text-darkTextSecondary mb-4">
                            {userData?.specialization?.name}
                        </p> :
                        <p className="text-gray-600 dark:text-darkTextSecondary mb-4">
                            {userData?.role?.title}
                        </p>
                    }
                    <div className="flex justify-center gap-2">
                        <span className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                            {language ? 'Active' : 'نشط'}
                        </span>
                    </div>
                </div>
            </div>
            {/* User Main Info */}
            <div className="bg-white dark:bg-darkCard rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-darkText mb-6">
                    {language ? 'Personal Information' : 'المعلومات الشخصية'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkTextSecondary mb-2">
                            {language ? 'Full Name' : 'الاسم الكامل'}
                        </label>
                        {/* {isEditing ? (
                            <input
                                type="text"
                                value={profileData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-darkBorder rounded-lg focus:ring-2 focus:ring-watomsBlue focus:border-transparent dark:bg-darkCard dark:text-darkText"
                            />
                        ) : ( */}
                        <p className="text-gray-800 dark:text-darkText">{userData?.first_name} {userData?.middle_name} {userData?.last_name}</p>
                        {/* )} */}
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-darkTextSecondary mb-2">
                            {language ? 'Email' : 'البريد الإلكتروني'}
                        </label>
                        {/* {isEditing ? (
                            <input
                                type="email"
                                value={profileData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className="overflow-hidden w-full px-4 py-2 border border-gray-300 dark:border-darkBorder rounded-lg focus:ring-2 focus:ring-watomsBlue focus:border-transparent dark:bg-darkCard dark:text-darkText"
                            />
                        ) : ( */}
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                            <p className="overflow-hidden text-gray-800 dark:text-darkText">{userData?.email}</p>
                        </div>
                        {/* )} */}
                    </div>

                    {/* Position */}
                    <div className="text-black">
                        {role === "Student" ?
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkTextSecondary mb-2">
                                {language ? 'Specialization' : 'التخصص'}
                            </label> :
                            <label className="block text-sm font-medium text-gray-700 dark:text-darkTextSecondary mb-2">
                                {language ? 'Position' : 'المنصب'}
                            </label>
                        }
                        {/* {isEditing ? (
                            <input
                                type="text"
                                value={profileData.position}
                                onChange={(e) => handleInputChange('position', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-darkBorder rounded-lg focus:ring-2 focus:ring-watomsBlue focus:border-transparent dark:bg-darkCard dark:text-darkText"
                            />
                        ) : ( */}
                        {role === "Student" ?
                            userData?.specialization?.name :
                            userData?.role?.title
                        }
                        {/* )} */}
                    </div>
                </div>
            </div>

            {/* Personal Employee Data */}
            <div className="mt-6 bg-white dark:bg-darkCard rounded-2xl shadow-xl p-6" dir="rtl">
                <h3 className="text-xl font-bold text-gray-800 dark:text-darkText mb-6">
                    البيانات الشخصية
                </h3>

                <div className="space-y-4 text-gray-800 dark:text-darkText">
                    <p>
                        <strong>الرقم القومي (14 رقماً): </strong>
                        {valueOrDots(userData?.id_number)}
                    </p>

                    <p>
                        <strong>تاريخ الميلاد: </strong>
                        {valueOrDots(userData?.birth_date)}
                        {" "}
                        <strong>محل الميلاد: </strong>
                        {valueOrDots(userData?.birth_place)}
                    </p>

                    <p>
                        <strong>الجنسية: </strong>
                        {valueOrDots(userData?.nationality)}
                        {" "}
                        <strong>الديانة: </strong>
                        {valueOrDots(userData?.religion)}
                    </p>

                    <p>
                        <strong>العنوان الحالي: </strong>
                        {valueOrDots(userData?.address)}
                    </p>

                    <p>
                        <strong>رقم الهاتف المحمول: </strong>
                        {valueOrDots(userData?.phone_number)}
                    </p>
                </div>
            </div>

            {/* Employment Status Data */}
            {role !== "Student" && <div className="mt-6 bg-white dark:bg-darkCard rounded-2xl shadow-xl p-6" dir="rtl">
                <h3 className="text-xl font-bold text-gray-800 dark:text-darkText mb-6">
                    الموقف التوظيفي الحالي
                </h3>

                <div className="space-y-4 text-gray-800 dark:text-darkText">
                    <p>
                        <strong>الكود الوظيفي: </strong>
                        {valueOrDots(userData?.id)}
                    </p>

                    <p>
                        <strong>المسمى الوظيفي الحالي: </strong>
                        {valueOrDots(userData?.role?.title)}
                    </p>

                    <p>
                        <strong>القسم / الورشة التابع لها: </strong>
                        {valueOrDots(null)}
                    </p>

                    <p>
                        <strong>الدرجة الوظيفية المالية: </strong>
                        {valueOrDots(userData?.financial_job_level)}
                    </p>

                    <p>
                        <strong>المجموعة النوعية: </strong>
                        {valueOrDots(userData?.qualitative_group)}
                    </p>

                    <p>
                        <strong>تاريخ التعيين الفعلي: </strong>
                        {valueOrDots(userData?.appointment_date)}
                    </p>

                    <p>
                        <strong>تاريخ استلام العمل الحالي: </strong>
                        {valueOrDots(userData?.date_of_receipt_of_current_work)}
                    </p>

                    <p>
                        <strong>نوع التعاقد: </strong>
                        {valueOrDots(userData?.contract_type)}
                    </p>
                </div>
            </div>}
        </div>
    )
};

export default UserProfileData;